<script setup lang="ts">
  import { computed, getCurrentInstance, ref } from 'vue';
  import { IRestaurant } from '@/interfaces/IRestaurant';
  import { useMenuStore } from '@/composition/use-menu';
  import { useMenuV2 } from '@/composition/use-menu-v2';
  import { IMenuPageTarget, menuPagePath } from '@/domain/catalog/menu-links';
  import { useRestaurantStore } from '@/stores/restaurant.store';

  // i18n in script context (script setup has no $t)
  const proxy = getCurrentInstance()!.proxy;

  // Props
  const props = defineProps<{
    isNewStore: boolean;
    itemCount: number;
    categoryCount: number;
  }>();

  // Stores
  const menuStore = useMenuStore();
  const restaurantStore = useRestaurantStore();
  const { isMenuV2Active } = useMenuV2();

  /** The Menú page (with the deep link) or the legacy Items / Categorías. */
  function menuLink(target: IMenuPageTarget): string {
    return menuPagePath(
      restaurantStore.restaurant?.stringId ?? '',
      isMenuV2Active.value,
      target
    );
  }

  // Computed
  const improvementSuggestions = computed(() => {
    if (!restaurantStore.restaurant || !menuStore.visibleItems) return [];

    const totalItems = menuStore.visibleItems.length;
    const isPaid = restaurantStore.effectiveTier > 0;
    const branch = restaurantStore.selectedBranch;

    const progress = {
      itemsWithPhotos: 0,
      itemsWithDescriptions: 0,
    };

    for (const item of menuStore.visibleItems) {
      if (item.picture) progress.itemsWithPhotos++;
      if (item.description) progress.itemsWithDescriptions++;
    }

    const iaTranslationsEnabled =
      isPaid && !!restaurantStore.restaurant.languageIds?.length;
    const orderAllowed = isPaid && branch?.allowOrders;
    const hasSocialNetworks = isPaid && !!branch?.links?.length;

    const suggestions = [];

    // Always show these for existing stores with items
    if (totalItems > 0) {
      suggestions.push({
        id: 'photos',
        title: String(proxy.$t('dashboard.onbPhotos')),
        progress: (progress.itemsWithPhotos / totalItems) * 100,
        complete: progress.itemsWithPhotos === totalItems,
        tooltip: String(
          proxy.$t('dashboard.onbPhotosProgress', {
            done: progress.itemsWithPhotos,
            total: totalItems,
          })
        ),
        icon: 'mdi-camera',
        link: menuLink({ filter: 'no-photo' }),
      });

      suggestions.push({
        id: 'descriptions',
        title: String(proxy.$t('dashboard.onbDescriptions')),
        progress: (progress.itemsWithDescriptions / totalItems) * 100,
        complete: progress.itemsWithDescriptions === totalItems,
        tooltip: String(
          proxy.$t('dashboard.onbDescriptionsProgress', {
            done: progress.itemsWithDescriptions,
            total: totalItems,
          })
        ),
        icon: 'mdi-format-text',
        link: menuLink({ filter: 'no-description' }),
      });
    }

    // Show categories/items only for new stores or if incomplete
    if (props.isNewStore || props.categoryCount === 0) {
      suggestions.push({
        id: 'categories',
        title: String(proxy.$t('dashboard.onbCategories')),
        progress: props.categoryCount > 0 ? 100 : 0,
        complete: props.categoryCount > 0,
        tooltip:
          props.categoryCount > 0
            ? String(
                proxy.$t('dashboard.onbCategoriesDone', {
                  count: props.categoryCount,
                })
              )
            : String(proxy.$t('dashboard.onbCategoriesHint')),
        icon: 'mdi-menu',
        link: menuLink({ category: 'new' }),
      });
    }

    if (props.isNewStore || props.itemCount === 0) {
      suggestions.push({
        id: 'items',
        title: String(proxy.$t('dashboard.onbItems')),
        progress: props.itemCount > 0 ? 100 : 0,
        complete: props.itemCount > 0,
        tooltip:
          props.itemCount > 0
            ? String(
                proxy.$t('dashboard.onbItemsDone', { count: props.itemCount })
              )
            : String(proxy.$t('dashboard.onbItemsHint')),
        icon: 'mdi-silverware',
        link: menuLink({ item: 'new' }),
      });
    }

    // QR only for new stores
    if (props.isNewStore) {
      suggestions.push({
        id: 'qr',
        title: String(proxy.$t('dashboard.onbQr')),
        complete: false,
        icon: 'mdi-qrcode',
        tooltip: String(proxy.$t('dashboard.onbQrHint')),
        link: `/${restaurantStore.restaurant.stringId}/qr`,
      });
    }

    // Always show these improvement suggestions
    suggestions.push({
      id: 'translations',
      title: String(proxy.$t('dashboard.onbTranslations')),
      complete: iaTranslationsEnabled,
      icon: 'mdi-web',
      tooltip: iaTranslationsEnabled
        ? String(
            proxy.$t('dashboard.onbTranslationsDone', {
              count: restaurantStore.restaurant.languageIds?.length,
            })
          )
        : String(proxy.$t('dashboard.onbTranslationsHint')),
      link: `/${restaurantStore.restaurant.stringId}/catalog/settings`,
    });

    suggestions.push({
      id: 'social',
      title: String(proxy.$t('dashboard.onbSocial')),
      complete: hasSocialNetworks,
      icon: 'mdi-share-variant',
      tooltip: hasSocialNetworks
        ? String(proxy.$t('dashboard.onbSocialDone'))
        : String(proxy.$t('dashboard.onbSocialHint')),
      link: `/${restaurantStore.restaurant.stringId}/settings?tab=info`,
    });

    suggestions.push({
      id: 'orders',
      title: String(proxy.$t('dashboard.onbOrders')),
      complete: orderAllowed,
      icon: 'mdi-cart',
      tooltip: orderAllowed
        ? String(proxy.$t('dashboard.onbOrdersDone'))
        : String(proxy.$t('dashboard.onbOrdersHint')),
      link: `/${restaurantStore.restaurant.stringId}/settings?tab=orders`,
    });

    return suggestions;
  });

  const overallProgress = computed(() => {
    if (!improvementSuggestions.value.length) return 0;

    const totalWeight = improvementSuggestions.value.reduce(
      (sum, suggestion) => {
        // Give more weight to critical items for new stores
        if (
          props.isNewStore &&
          ['categories', 'items', 'qr'].includes(suggestion.id)
        ) {
          return sum + 2;
        }
        return sum + 1;
      },
      0
    );

    const completedWeight = improvementSuggestions.value.reduce(
      (sum, suggestion) => {
        const weight =
          props.isNewStore &&
          ['categories', 'items', 'qr'].includes(suggestion.id)
            ? 2
            : 1;
        if (suggestion.complete) return sum + weight;
        if (suggestion.progress)
          return sum + (suggestion.progress / 100) * weight;
        return sum;
      },
      0
    );

    return Math.round((completedWeight / totalWeight) * 100);
  });

  // Highlight card for new stores until every suggestion is at least started
  const highlightCard = computed(() => {
    if (!props.isNewStore) return false;
    if (!improvementSuggestions.value.length) return false;
    // If there is any suggestion that has not been started (no progress and not complete), keep highlighting
    return improvementSuggestions.value.some(
      (s) => !s.complete && (!s.progress || s.progress === 0)
    );
  });
</script>

<template>
  <div class="onboarding-improvement">
    <!-- Main Improvement Card -->
    <v-card
      class="improvement-card"
      :class="{ 'glow-border-orange': highlightCard }"
      elevation="0"
    >
      <v-card-title class="card-header">
        <div class="header-content">
          <div class="header-left">
            <v-icon class="header-icon">mdi-lightbulb-on</v-icon>
            <span class="header-title">
              {{
                isNewStore
                  ? $t('dashboard.onbSetupTitle')
                  : $t('dashboard.onbImproveTitle')
              }}
            </span>
          </div>
        </div>
      </v-card-title>
      <v-card-text class="card-content">
        <div class="overall-progress-section">
          <v-progress-linear
            :value="overallProgress"
            :color="overallProgress === 100 ? 'success' : 'orange'"
            height="8"
            rounded
            class="overall-progress"
          >
            <template v-slot:default="{ value }">
              <span class="progress-text">{{ Math.round(value) }}%</span>
            </template>
          </v-progress-linear>
        </div>

        <div class="suggestions-list">
          <template v-if="!improvementSuggestions.length">
            <v-skeleton-loader type="list-item-avatar" />
            <v-skeleton-loader type="list-item-avatar" />
          </template>
          <template v-else>
            <div
              v-for="suggestion in improvementSuggestions"
              :key="suggestion.id"
              class="suggestion-item"
              :class="{ 'is-complete': suggestion.complete }"
            >
              <!-- Progress/Complete Indicator -->
              <div class="status-indicator">
                <div v-if="suggestion.complete" class="complete-badge">
                  <v-icon small color="white">mdi-check</v-icon>
                </div>
                <v-progress-circular
                  v-else
                  :value="suggestion.progress ?? 0"
                  :color="suggestion.progress > 0 ? 'orange' : 'grey'"
                  :size="24"
                  :width="3"
                  class="progress-circle"
                />
              </div>

              <!-- Content -->
              <div class="suggestion-content">
                <router-link :to="suggestion.link" class="suggestion-title">
                  {{ suggestion.title }}
                </router-link>
                <span class="suggestion-tooltip">
                  {{ suggestion.tooltip }}
                </span>
              </div>

              <!-- Action Button -->
              <v-btn
                v-if="!suggestion.complete"
                color="orange"
                small
                icon
                :to="suggestion.link"
                class="action-btn"
              >
                <v-icon small>mdi-arrow-right</v-icon>
              </v-btn>
            </div>
          </template>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<style scoped lang="scss">
  .onboarding-improvement {
    @apply tw-space-y-4;

    .improvement-card {
      @apply tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-neutral-100;

      .card-header {
        @apply tw-pb-2 tw-pt-3;

        .header-content {
          @apply tw-flex tw-items-center tw-justify-between tw-w-full;

          .header-left {
            @apply tw-flex tw-items-center tw-gap-2;

            .header-icon {
              @apply tw-text-orange-500;
              font-size: 20px;
            }

            .header-title {
              @apply tw-text-base tw-font-semibold tw-text-neutral-800;
            }
          }
        }
      }

      .card-content {
        @apply tw-pt-0;

        .overall-progress-section {
          @apply tw-mb-4;

          .overall-progress {
            .progress-text {
              @apply tw-text-xs tw-font-medium tw-text-white;
            }
          }
        }
      }

      .suggestions-list {
        @apply tw-space-y-2;

        .suggestion-item {
          @apply tw-flex tw-items-center tw-space-x-3;
          @apply tw-p-2 tw-rounded-lg tw-transition-all tw-duration-200;

          &:hover {
            @apply tw-bg-neutral-50;
          }

          &.is-complete {
            .suggestion-content {
              .suggestion-title {
                @apply tw-text-neutral-500;
              }
            }
          }

          .status-indicator {
            @apply tw-flex-shrink-0;

            .complete-badge {
              @apply tw-bg-green-500 tw-rounded-full;
              @apply tw-h-6 tw-w-6 tw-flex tw-items-center tw-justify-center;
            }

            .progress-circle {
              @apply tw-block;
            }
          }

          .suggestion-content {
            @apply tw-flex-1 tw-min-w-0;

            .suggestion-title {
              @apply tw-block tw-font-medium tw-text-neutral-700;
              @apply tw-no-underline hover:tw-text-orange-600;
              @apply tw-transition-colors tw-duration-200;
            }

            .suggestion-tooltip {
              @apply tw-text-xs tw-text-neutral-500 tw-block;
            }
          }

          .action-btn {
            @apply tw-flex-shrink-0;
            @apply tw-opacity-0 tw-transition-opacity tw-duration-200;
          }

          &:hover .action-btn {
            @apply tw-opacity-100;
          }
        }
      }
    }

    /* glow-border-orange class is now global */
  }
</style>
